import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import { Subject } from "rxjs/Subject";
import "rxjs/add/operator/takeUntil";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import SelectFormInput from "../../../../models/SelectFormInput";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { OrderForm } from "../order-form/order-form";

@Component({
  selector: "dme-order-form",
  templateUrl: "dme-order-form.html",
})
export class DmeOrderFormComponent extends OrderForm
  implements OnInit, OnDestroy {
  protected incomingData: any = {
    category: "",
    physician: {},
    administeredBy: {},
  };

  // Completed in ngOnDestroy to tear down every subscription this component
  // opens. The form is created/destroyed each time the Add Order modal's
  // order-type ngSwitch changes (or the modal reopens), so the constructor's
  // valueChanges subscriptions and the physician-search HTTP subscriptions are
  // routed through takeUntil(this.destroy$) so nothing survives the component.
  // (Declared as a field so it is initialized before the constructor body's
  // subscriptions run.)
  private destroy$ = new Subject<void>();

  protected orderForm: FormGroup;
  private textMaxLength = 300;
  private textAreaMaxLength = 1000;
  private startDateMin: string = "";
  private startDateMax: string = "";
  private endDateMin: string = "";
  private endDateMax: string = "";

  private displayedIssuesOptions: any[] = [];

  hideIssuesOptions: boolean = true;
  defaultStartTime: string = "";

  hidePhysiciansOptions: Boolean = true;
  searchTerm: string = "";
  selectPhysicianFlag: boolean = false;
  setPhysicianName: boolean = false;

  hideDcPhysiciansOptions: Boolean = true;
  dcSearchTerm: string = "";
  selectDcPhysicianFlag: boolean = false;
  setDcPhysicianName: boolean = false;

  private physicians: any = [];

  @Input() categoryOptions: SelectFormInput[] = [];
  @Input() issuesOptions: SelectFormInput[] = [];
  @Input() physicianOptions: SelectFormInput[] = [];
  @Input() administeredByOptions: SelectFormInput[] = [];
  @Input() physicianOrderOptions: SelectFormInput[] = [];
  @Input() status: any;
  @Input() isOpenOrderEdit: boolean = false;
  @Input() isActiveOrderEdit: boolean = false;
  @Input() isIpad: boolean = false;
  @Output() onValidityChange: EventEmitter<any> = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private resourceManagementService: ResourceManagementService,
    public utilityService: UtilityService
  ) {
    super();

    this.startDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.startDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.endDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.endDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.defaultStartTime = moment().format("YYYY-MM-DD");

    this.orderForm = this.formBuilder.group({
      categories: ["", Validators.compose([Validators.required])],
      issue: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      dmeType: [
        "",
        Validators.compose([
          Validators.maxLength(this.textMaxLength),
          Validators.required,
        ]),
      ], //TODO autocomplete
      itemStartDatetime: ["", Validators.compose([Validators.required])],
      itemDiscontinueDatetime: [""],
      physician: ["", Validators.compose([Validators.required])],
      hospicePayer: [false], //required field but it's boolean
      administeredBy: ["", Validators.compose([Validators.required])],
      teachingsPerformed: [false],
      acdApproval: [false],
      description: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength)]),
      ],
      physicianOrder: ["", Validators.compose([Validators.required])],
      physicianOrderNotes: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      searchTerm: new FormControl(""),
      dcSearchTerm: new FormControl(""),
    });

    this.orderForm.valueChanges.takeUntil(this.destroy$).subscribe((data) => {
      this.onValidityChange.emit({
        validity: this.orderForm.valid,
        data: data,
      });
    });

    //enforce character limit
    this.orderForm.controls["dmeType"].valueChanges.takeUntil(this.destroy$).subscribe((data) => {
      if (data.length > this.textMaxLength) {
        this.orderForm.controls["dmeType"].setValue(
          data.substring(0, this.textMaxLength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["description"].valueChanges.takeUntil(this.destroy$).subscribe((data) => {
      if (data.length > this.textAreaMaxLength) {
        this.orderForm.controls["description"].setValue(
          data.substring(0, this.textAreaMaxLength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["physicianOrderNotes"].valueChanges.takeUntil(this.destroy$).subscribe(
      (data) => {
        if (data.length > this.textAreaMaxLength / 2) {
          this.orderForm.controls["physicianOrderNotes"].setValue(
            data.substring(0, this.textAreaMaxLength / 2)
          );
        }
      }
    );

    //enforce startDate cannot be after endDate
    this.orderForm.controls["itemStartDatetime"].valueChanges.takeUntil(this.destroy$).subscribe(
      (data) => {
        this.endDateMin = moment(data).format("YYYY-MM-DD");
      }
    );

    //enforce endDate cannot be before startDate
    this.orderForm.controls["itemDiscontinueDatetime"].valueChanges.takeUntil(this.destroy$).subscribe(
      (data) => {
        this.startDateMax = moment(data).format("YYYY-MM-DD");
      }
    );

    //if user switches category clear issue field
    this.orderForm.controls["categories"].valueChanges.takeUntil(this.destroy$).subscribe(() => {
      this.orderForm.controls["issue"].setValue("");
    });
  }

  ngOnInit() {
    if (this.incomingData) {
      if (this.incomingData.rmPhysician) {
        this.searchTerm = this.incomingData.rmPhysician.displayName;
      }

      //apply limits to prevent startDate > endDate if data exists
      let startDatetime = this.incomingData.itemStartDatetime;
      if (startDatetime) {
        this.endDateMin = moment(startDatetime).format("YYYY-MM-DD");
      }
      let endDatetime = this.incomingData.itemDiscontinueDatetime;
      if (endDatetime) {
        this.startDateMax = moment(endDatetime).format("YYYY-MM-DD");
      }
    }

    // if discontinue make itemDiscontinueDatetime required
    if (this.status === "discontinue") {
      let discDate = this.orderForm.get("itemDiscontinueDatetime");
      discDate.setValidators([Validators.required]);
      discDate.updateValueAndValidity();
      this.orderForm.addControl(
        "dcPhysician",
        new FormControl("", Validators.required)
      );
      this.orderForm.controls["dcPhysician"].updateValueAndValidity();
    }
  }

  ngOnDestroy() {
    // Tear down every subscription opened via takeUntil(this.destroy$): the
    // constructor's form valueChanges streams and the physician-search HTTP
    // subscriptions. Prevents a destroyed form (order-type switch / modal
    // reopen) from still emitting onValidityChange or mutating physicians.
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ---- issue autocomplete ---- */

  searchIssues(searchTerm: string) {
    if (this.orderForm.controls["categories"].value === "") {
      //default search if no category is selected
      if (
        this.displayedIssuesOptions.filter(
          (issue) => issue.description === searchTerm
        ).length === 0 &&
        searchTerm !== ""
      ) {
        this.hideIssuesOptions = false;
        this.displayedIssuesOptions = this.issuesOptions.filter((issue) =>
          issue["description"].toLowerCase().includes(searchTerm.toLowerCase())
        );
      } else {
        this.hideIssuesOptions = true;
      }
    } else {
      let category = this.orderForm.controls["categories"].value;
      if (
        this.displayedIssuesOptions.filter(
          (issue) => issue.description === searchTerm
        ).length === 0 &&
        searchTerm !== ""
      ) {
        this.hideIssuesOptions = false;
        this.displayedIssuesOptions = this.issuesOptions.filter((issue) => {
          let categoryObj = this.categoryOptions.find((option) => {
            return option.value === category;
          });
          // A selected category value with no matching categoryOptions entry
          // (e.g. a stale prefilled category vs. asynchronously-loaded options)
          // must not crash the autocomplete — filter to nothing instead of
          // dereferencing categoryObj.display.
          // Null-safe read of the section-type lookup: a malformed issue with
          // no lookups / empty sectionTypeLookupCode must filter out, not throw.
          const sectionValue =
            issue["lookups"] &&
            issue["lookups"]["sectionTypeLookupCode"] &&
            issue["lookups"]["sectionTypeLookupCode"][0]
              ? issue["lookups"]["sectionTypeLookupCode"][0]["value"]
              : undefined;
          return (
            issue["description"]
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) &&
            !!categoryObj &&
            sectionValue === categoryObj.display
          );
        });
      } else {
        this.hideIssuesOptions = true;
      }
    }
  }

  setIssue(issue: any) {
    this.orderForm.controls["issue"].setValue(issue.description);
    this.hideIssuesOptions = true;
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hidePhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getPhysicians(searchTerm) {
    if (!this.selectPhysicianFlag) {
      this.selectPhysicianFlag = false;
      this.setPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .takeUntil(this.destroy$)
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectPhysicianFlag = false;
    }
  }

  setPhysician(physician: any) {
    this.selectPhysicianFlag = true;
    this.setPhysicianName = false;
    this.hidePhysiciansOptions = true;
    this.orderForm.controls["physician"].setValue(physician);
    this.orderForm.controls["searchTerm"].setValue(physician.displayName);
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchDcPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hideDcPhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getDcPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getDcPhysicians(searchTerm) {
    if (!this.selectDcPhysicianFlag) {
      this.selectDcPhysicianFlag = false;
      this.setDcPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .takeUntil(this.destroy$)
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectDcPhysicianFlag = false;
    }
  }

  setDcPhysician(physician: any) {
    this.selectDcPhysicianFlag = true;
    this.setDcPhysicianName = false;
    this.hideDcPhysiciansOptions = true;
    this.orderForm.controls["dcPhysician"].setValue(physician);
    this.orderForm.controls["dcSearchTerm"].setValue(physician.displayName);
  }
}



// WEBPACK FOOTER //
// ./src/components/orders/forms/dme-order-form/dme-order-form.ts